'use client'

import { Dialog, type DialogAPI } from '@fukku/dialog/Dialog'
import { useClosingAnimation } from '@fukku/dialog/hooks/useClosingAnimation'
import type { DialogProps } from '@fukku/dialog/types/props'
import { cx } from 'classix'
import { forwardRef, useImperativeHandle, useRef } from 'react'

import { DialogContextProvider, DialogType } from '../hooks/useDialogContext'
import { apiFactory } from '../utils/apiFactory'
import styles from './Modal.module.css'

export function ModalComponent(
	{
		children,
		title,
		preventClose = false,
		keepContent = false,
		onOpen,
		onClose,
		'data-testid': dataTestId,
		className,
		isFullScreen = false,
		targetLayer,
	}: DialogProps & { readonly isFullScreen?: boolean },
	ref: React.Ref<DialogAPI>
) {
	const dialogRef = useRef<HTMLDialogElement>(null)

	const { opened, closing, setOpened } = useClosingAnimation(dialogRef)

	const api = apiFactory(setOpened, dialogRef.current)

	useImperativeHandle(ref, () => api, [api])

	return (
		<DialogContextProvider
			type={DialogType.SimpleDialog}
			api={api}
			preventClose={preventClose}
		>
			<Dialog
				title={title}
				className={cx(
					styles.modal,
					closing && styles.closing,
					isFullScreen && styles.fullscreen,
					className
				)}
				ref={dialogRef}
				preventClose={preventClose}
				keepContent={keepContent}
				setOpened={setOpened}
				opened={opened}
				onOpen={onOpen}
				onClose={onClose}
				data-testid={dataTestId}
				targetLayer={targetLayer}
			>
				<div className={styles.inner}>{children}</div>
			</Dialog>
		</DialogContextProvider>
	)
}

export const Modal = forwardRef(ModalComponent)
